/**
 *
 * @param {Object | HelperText} curr - The current helper text object
 * @param {string} acc - The accumulated string
 * @param {function} getValues - function that takes a key and returns a value from form object
 * @returns string
 */

const testObject = (curr, acc, getValues) => {
    if (curr instanceof Object) {
        // If it doesn't have a type, it's not a valid helper entry, so skip it
        if (!Object.hasOwn(curr, 'type')) return acc;
        if (curr.type === 'text' && typeof curr.text === 'string') return `${acc}${curr.text}`;
        if (curr.type === 'key' && getValues instanceof Function) return `${acc}${curr.key ? getValues(curr.key) : ''}`;
    }

    return acc;
};

/**
 * @typedef {Object} HelperText
 * @property {string} type - The type of helper text. Can be 'text' or 'key'
 * @property {string} text - The text to display
 * @property {string} key - The key to get the value from the form object
 */

/**
 * Parses the helper text for a form input. Invalid input is ignored.
 * @param {HelperText} helperText - Can be a string, an array of strings, or an array of objects
 * @param {function} getValues - A function that takes a key and returns a value from form object
 * @returns string
 */
export const getHelperTextFromConfig = (helperText, getValues) => {
    if (!helperText || typeof helperText === 'string') return helperText;
    if (helperText instanceof Array)
        return helperText.reduce((acc, curr) => {
            if (typeof curr === 'string') return `${acc}${curr}`;
            return testObject(curr, acc, getValues);
        }, '');
    return testObject(helperText, '', getValues);
};

export const prorateAmount = (totalAmount, sequence, key = 'expected_amount') => {
    const sequenceSum = sequence.reduce((acc, field) => acc + (field[key] ?? 0), 0);
    if (sequenceSum === totalAmount) {
        return sequence;
    }
    const adjustedSequence = sequence.map((field) => {
        if (totalAmount === 0 || sequenceSum === 0) {
            return field;
        }
        const expected_amount = Math.floor((field[key] / sequenceSum) * totalAmount);
        return {
            ...field,
            [key]: expected_amount,
        };
    });
    const adjustedSequenceSum = adjustedSequence.reduce((acc, field) => acc + (field.expected_amount ?? 0), 0);
    const lastProducerIndex = adjustedSequence.length - 1;
    const remainder = totalAmount - adjustedSequenceSum;

    // We are rounding down all the expected amounts
    // Add the remainder to the last producer's expected amount
    if (remainder > 0) {
        adjustedSequence[lastProducerIndex].expected_amount += remainder;
    }
    return adjustedSequence;
};

export const formatNumber = (value, defaultZero) => {
    // If defaultZero, return 0 if value is undefined or null
    if (value === undefined || value === null) {
        return defaultZero ? 0 : value;
    }
    return parseFloat(value);
};

export default formatNumber;
